import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import MainLayout from '../components/layout/MainLayout';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import { GuidedAction } from '../components/enterprise';
import {
  Tooltip as UITooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../components/ui/tooltip';
import {
  ChevronRight, ArrowRight, CheckCircle, ExternalLink,
  TrendingUp, TrendingDown, ShieldAlert, Shield, AlertTriangle, ChevronDown,
  Target, Lock, BarChart3, FileText, Download, Activity, Gauge,
  Clock, Users, Zap, Eye, ArrowUpRight, RefreshCw
} from 'lucide-react';
import { generateAssessmentPDF } from '../utils/generatePDF';
import { generateCompliancePDF } from '../utils/generateCompliancePDF';
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, BarChart, Bar, Cell,
  Tooltip as RechartsTooltip
} from 'recharts';

const SEVERITY_COLORS = {
  kritiek: { bg: 'bg-red-500', text: 'text-red-700', light: 'bg-red-50', border: 'border-red-200', dot: 'bg-red-500' },
  hoog: { bg: 'bg-orange-500', text: 'text-orange-700', light: 'bg-orange-50', border: 'border-orange-200', dot: 'bg-orange-500' },
  midden: { bg: 'bg-amber-500', text: 'text-amber-700', light: 'bg-amber-50', border: 'border-amber-200', dot: 'bg-amber-500' },
  laag: { bg: 'bg-emerald-500', text: 'text-emerald-700', light: 'bg-emerald-50', border: 'border-emerald-200', dot: 'bg-emerald-500' },
  miniem: { bg: 'bg-sky-500', text: 'text-sky-700', light: 'bg-sky-50', border: 'border-sky-200', dot: 'bg-sky-500' },
};

const RISK_CLASSES = {
  laag: { color: 'text-emerald-600', bg: 'bg-emerald-50' },
  beperkt: { color: 'text-green-600', bg: 'bg-green-50' },
  verhoogd: { color: 'text-amber-600', bg: 'bg-amber-50' },
  hoog: { color: 'text-orange-600', bg: 'bg-orange-50' },
  kritiek: { color: 'text-red-600', bg: 'bg-red-50' },
};

const DOMAIN_LABELS = {
  governance: 'Governance', mens: 'Mens', proces: 'Proces',
  techniek: 'Techniek', risicobeheersing: 'Risicobeheersing', continuiteit: 'Continuïteit',
};

const STATUS_LABELS = {
  kritiek: 'Kritiek', onvoldoende_gegevens: 'Onvoldoende gegevens', aandacht: 'Aandacht vereist', gezond: 'Gezond',
};
const STATUS_COLORS = {
  kritiek: 'bg-red-500 text-white', onvoldoende_gegevens: 'bg-slate-400 text-white',
  aandacht: 'bg-amber-500 text-white', gezond: 'bg-emerald-500 text-white',
};

const DashboardPage = () => {
  const { api, user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [scoreData, setScoreData] = useState(null);
  const [chainData, setChainData] = useState({ nodes: {}, edges: [], insights: {}, action_items: [] });
  const [progressData, setProgressData] = useState(null);
  const [trendData, setTrendData] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);

  const downloadPDF = async () => {
    setPdfLoading(true);
    try {
      const res = await api.get('/scores/report/pdf');
      generateAssessmentPDF(res.data);
      toast.success('Rapport gedownload');
    } catch { toast.error('PDF generatie mislukt'); }
    finally { setPdfLoading(false); }
  };

  const downloadCompliancePDF = async () => {
    try {
      const res = await api.get('/scores/compliance/report');
      if (!res.data.frameworks?.length) { toast.error('Geen actieve frameworks'); return; }
      generateCompliancePDF(res.data);
      toast.success('Compliance rapport gedownload');
    } catch { toast.error('Export mislukt'); }
  };

  const fetchData = useCallback(async () => {
    try {
      const [scoreRes, chainRes, progressRes, trendRes] = await Promise.all([
        api.get('/scores/dashboard').catch(() => ({ data: null })),
        api.get('/ggrc/chain'),
        api.get('/ggrc/progress').catch(() => ({ data: null })),
        api.get('/dashboard/trends').catch(() => ({ data: null })),
      ]);
      setScoreData(scoreRes.data);
      setChainData(chainRes.data);
      setProgressData(progressRes.data);
      setTrendData(trendRes.data);
      setLastUpdated(new Date());
    } catch (e) { console.error('Dashboard fetch error:', e); }
    finally { setLoading(false); }
  }, [api]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const { nodes, insights } = chainData;
  const totalRisks = (nodes.risks || []).length;
  const uncoveredRisks = insights.uncovered_risks || 0;
  const highRisks = insights.high_risks || 0;

  const totalStatus = scoreData?.total_status || {};
  const riskScore = scoreData?.risk?.org_score;
  const riskClass = RISKS_CLASSES_GET(scoreData?.risk?.org_class);
  const maturityLevel = scoreData?.maturity?.org_level;
  const confidence = scoreData?.confidence?.general || 0;
  const coverage = scoreData?.coverage || {};
  const nulmetingPct = progressData?.nulmeting_pct || 0;

  // Risk severity breakdown — lees severity OF risk_level (afhankelijk van endpoint)
  const LEVEL_MAP = { critical: 'kritiek', high: 'hoog', medium: 'midden', low: 'laag', minimal: 'miniem' };
  const risksByLevel = { kritiek: 0, hoog: 0, midden: 0, laag: 0 };
  (nodes.risks || []).forEach(r => {
    const raw = r.severity || r.risk_level || 'laag';
    const lvl = LEVEL_MAP[raw] || raw;
    if (risksByLevel[lvl] !== undefined) risksByLevel[lvl]++;
  });

  // Action items
  const actionItems = chainData.action_items || [];
  const topActions = actionItems.slice(0, 5);

  // Measure stats
  const measureStats = { total: 0, done: 0 };
  (nodes.measures || []).forEach(m => {
    measureStats.total++;
    if (m.status === 'implemented' || m.status === 'verified') measureStats.done++;
  });

  if (loading) {
    return (
      <MainLayout>
        <div className="min-h-screen">
          <div className="mx-auto max-w-[1400px] px-6 py-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h1 className="text-2xl font-semibold text-[#111827]">Security Overview</h1>
                <Skeleton className="h-4 w-48 mt-1 rounded" />
              </div>
              <div className="flex gap-2">
                <Skeleton className="h-10 w-24 rounded-lg" />
                <Skeleton className="h-10 w-24 rounded-lg" />
              </div>
            </div>
            <div className="grid grid-cols-5 gap-3 mb-6">{[1,2,3,4,5].map(i => <Skeleton key={i} className="h-24 rounded-xl" />)}</div>
            <Skeleton className="h-24 w-full rounded-xl mb-6" />
            <div className="grid grid-cols-3 gap-4">{[1,2,3].map(i => <Skeleton key={i} className="h-64 rounded-xl" />)}</div>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="min-h-screen" data-testid="dashboard-page">
        <div className="mx-auto max-w-[1400px] px-6 py-6">

          {/* PAGE HEADER */}
          <div className="flex items-center justify-between mb-6" data-testid="dashboard-header">
            <div>
              <h1 className="text-2xl font-semibold text-neutral-900">Security Overview</h1>
              <div className="flex items-center gap-3 mt-1">
                {lastUpdated && (
                  <span className="text-xs text-neutral-400 flex items-center gap-1">
                    <Clock className="h-3.5 w-3.5" /> Bijgewerkt {lastUpdated.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' })}
                  </span>
                )}
                <Badge className={`${STATUS_COLORS[totalStatus.status] || 'bg-slate-400 text-white'} text-xs px-2.5 py-0.5`} data-testid="total-status-badge">
                  {STATUS_LABELS[totalStatus.status] || 'Laden...'}
                </Badge>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" className="h-10 text-sm rounded-lg border-neutral-200" onClick={fetchData} data-testid="refresh-dashboard">
                <RefreshCw className="h-4 w-4 mr-1.5" /> Ververs
              </Button>
              <div className="relative">
                <Button variant="outline" className="h-10 text-sm rounded-lg border-neutral-200" onClick={downloadPDF} disabled={pdfLoading} data-testid="download-pdf-btn">
                  <Download className="h-4 w-4 mr-1.5" /> Export
                </Button>
              </div>
            </div>
          </div>

          {/* KPI STRIP — 5 drilldownable cards */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6" data-testid="kpi-strip">
            {/* Security Posture */}
            <button onClick={() => navigate('/risks')} className="bg-white rounded-xl border border-[#E5E7EB] p-4 text-left hover:border-[#D1D5DB] hover:shadow-sm transition-all group" data-testid="kpi-risk">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-medium text-[#6B7280] uppercase tracking-wider">Risicoscore</span>
                <ArrowUpRight className="h-3.5 w-3.5 text-[#D1D5DB] group-hover:text-[#6B7280] transition-colors" />
              </div>
              <p className={`text-2xl font-bold tabular-nums ${riskClass.color}`}>{riskScore != null ? Math.round(riskScore) : '-'}</p>
              <div className="flex items-center gap-1.5 mt-1">
                <span className={`h-1.5 w-1.5 rounded-full ${riskScore <= 20 ? 'bg-[#047857]' : riskScore <= 60 ? 'bg-[#D97706]' : 'bg-[#B91C1C]'}`} />
                <span className="text-xs text-[#6B7280]">{scoreData?.risk?.org_class || '-'} (0–100)</span>
              </div>
            </button>

            {/* Maturity */}
            <button onClick={() => navigate('/maturity')} className="bg-white rounded-xl border border-[#E5E7EB] p-4 text-left hover:border-[#D1D5DB] hover:shadow-sm transition-all group" data-testid="kpi-maturity">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-medium text-[#6B7280] uppercase tracking-wider">Volwassenheid</span>
                <ArrowUpRight className="h-3.5 w-3.5 text-[#D1D5DB] group-hover:text-[#6B7280] transition-colors" />
              </div>
              <p className="text-2xl font-bold tabular-nums text-[#111827]">{maturityLevel != null ? `Niv. ${maturityLevel}` : '-'}</p>
              <span className="text-xs text-[#6B7280]">{scoreData?.maturity?.maturity_index != null ? `Index ${scoreData.maturity.maturity_index}` : 'CMM 1–5'}</span>
            </button>

            {/* Confidence */}
            <div className="bg-white rounded-xl border border-[#E5E7EB] p-4" data-testid="kpi-confidence">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-medium text-[#6B7280] uppercase tracking-wider">Betrouwbaarheid</span>
              </div>
              <p className={`text-2xl font-bold tabular-nums ${confidence >= 85 ? 'text-[#047857]' : confidence >= 50 ? 'text-[#92400E]' : 'text-[#6B7280]'}`}>{Math.round(confidence)}%</p>
              <span className="text-xs text-[#6B7280]">Doel: ≥ 85%</span>
            </div>

            {/* Coverage */}
            <button onClick={() => navigate('/measures')} className="bg-white rounded-xl border border-[#E5E7EB] p-4 text-left hover:border-[#D1D5DB] hover:shadow-sm transition-all group" data-testid="kpi-coverage">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-medium text-[#6B7280] uppercase tracking-wider">Dekking</span>
                <ArrowUpRight className="h-3.5 w-3.5 text-[#D1D5DB] group-hover:text-[#6B7280] transition-colors" />
              </div>
              <p className={`text-2xl font-bold tabular-nums ${(coverage.formal_coverage || 0) >= 90 ? 'text-[#047857]' : 'text-[#92400E]'}`}>
                {coverage.formal_coverage != null ? `${Math.round(coverage.formal_coverage)}%` : '-'}
              </p>
              <span className="text-xs text-[#6B7280]">{measureStats.done}/{measureStats.total} maatregelen effectief</span>
            </button>

            {/* Critical Findings */}
            <button onClick={() => navigate('/risks')} className="bg-white rounded-xl border border-[#E5E7EB] p-4 text-left hover:border-[#D1D5DB] hover:shadow-sm transition-all group" data-testid="kpi-critical">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-medium text-[#6B7280] uppercase tracking-wider">Kritieke risico's</span>
                <ArrowUpRight className="h-3.5 w-3.5 text-[#D1D5DB] group-hover:text-[#6B7280] transition-colors" />
              </div>
              <p className={`text-2xl font-bold tabular-nums ${risksByLevel.kritiek > 0 ? 'text-[#B91C1C]' : 'text-[#111827]'}`}>{risksByLevel.kritiek + risksByLevel.hoog}</p>
              <div className="flex items-center gap-2 mt-1">
                <span className="text-xs text-[#B91C1C]">{risksByLevel.kritiek} kritiek</span>
                <span className="text-xs text-[#92400E]">{risksByLevel.hoog} hoog</span>
              </div>
            </button>
          </div>

          {/* GUIDED ACTION — "Dit is nu je belangrijkste actie" */}
          {(() => {
            // Determine the single most important action
            const criticalRisks = risksByLevel.kritiek + risksByLevel.hoog;
            const hasNulmeting = nulmetingPct < 100;
            const hasUncovered = uncoveredRisks > 0;
            const topAction = actionItems[0];

            if (hasNulmeting && nulmetingPct < 30) {
              return (
                <GuidedAction
                  title="Start je nulmeting om je beveiliging in kaart te brengen"
                  description="De nulmeting analyseert je mensen, processen en technologie. Zonder deze basis kan Spooker geen risico's identificeren of aanbevelingen doen."
                  actionLabel="Start nulmeting"
                  onAction={() => navigate('/beoordelen/nulmeting')}
                  duration="Ongeveer 40 minuten"
                  severity="info"
                  icon={Activity}
                  testId="guided-action-nulmeting"
                />
              );
            }
            if (criticalRisks > 0 && hasUncovered) {
              const topRisk = (nodes.risks || []).find(r => (r.severity || r.risk_level) === 'kritiek') || (nodes.risks || []).find(r => (r.severity || r.risk_level) === 'hoog');
              return (
                <GuidedAction
                  title={topRisk ? `Pak eerst "${topRisk.title}" aan` : `${criticalRisks} risico's vereisen directe aandacht`}
                  description={topRisk
                    ? `Dit is een ${topRisk.severity || topRisk.risk_level} risico zonder gekoppelde maatregel. Door dit risico te beoordelen bepaal je de impact, eigenaar en aanbevolen oplossing.`
                    : `Er zijn ${criticalRisks} hoge of kritieke risico's gevonden, waarvan ${uncoveredRisks} zonder maatregel. Beoordeel het meest urgente risico om je beveiligingshouding te verbeteren.`}
                  actionLabel="Risico beoordelen"
                  onAction={() => navigate('/risicos/overzicht')}
                  duration="Ongeveer 5 minuten"
                  severity="critical"
                  icon={ShieldAlert}
                  secondaryLabel="Alle risico's bekijken"
                  onSecondary={() => navigate('/risicos/overzicht')}
                  testId="guided-action-risk"
                />
              );
            }
            if (hasUncovered) {
              return (
                <GuidedAction
                  title={`${uncoveredRisks} risico's zonder maatregel, koppel een oplossing`}
                  description="Risico's zonder maatregel blijven onbeschermd. Koppel maatregelen om je dekking te verhogen en compliance te waarborgen."
                  actionLabel="Maatregelen koppelen"
                  onAction={() => navigate('/verbeteren/maatregelen')}
                  duration="2–3 minuten per risico"
                  severity="warning"
                  icon={Shield}
                  testId="guided-action-measures"
                />
              );
            }
            if (hasNulmeting) {
              return (
                <GuidedAction
                  title="Ga verder met je nulmeting"
                  description={`Je nulmeting is ${nulmetingPct}% voltooid. Rond de resterende onderdelen af om een compleet beeld van je beveiliging te krijgen.`}
                  actionLabel="Verder met analyse"
                  onAction={() => navigate('/beoordelen/nulmeting')}
                  duration="Nog enkele minuten"
                  severity="info"
                  icon={Activity}
                  testId="guided-action-continue"
                />
              );
            }
            if (topAction) {
              return (
                <GuidedAction
                  title={topAction.title}
                  description={topAction.description || 'Dit is je volgende aanbevolen stap om je beveiliging te verbeteren.'}
                  actionLabel="Bekijk actie"
                  onAction={() => topAction.link ? navigate(topAction.link) : navigate('/risks')}
                  severity="info"
                  icon={Zap}
                  testId="guided-action-next"
                />
              );
            }
            return null;
          })()}

          {/* MAIN GRID — 2/3 + 1/3 layout */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">

            {/* LEFT COLUMN (2/3) */}
            <div className="lg:col-span-2 space-y-4">

              {/* RISK SEVERITY BREAKDOWN */}
              <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="risk-breakdown">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <ShieldAlert className="h-4 w-4 text-slate-400" strokeWidth={1.5} />
                    <h3 className="text-sm font-semibold text-slate-800">Risico's per ernst</h3>
                    <Badge variant="outline" className="text-[10px]">{totalRisks} totaal</Badge>
                  </div>
                  <Button variant="ghost" size="sm" className="text-xs h-7 text-slate-500" onClick={() => navigate('/risicos/overzicht')} data-testid="go-to-risks">
                    Bekijk alle <ChevronRight className="h-3 w-3 ml-0.5" />
                  </Button>
                </div>
                <div className="grid grid-cols-4 gap-3">
                  {Object.entries(risksByLevel).map(([level, count]) => {
                    const cfg = SEVERITY_COLORS[level] || SEVERITY_COLORS.laag;
                    return (
                      <button key={level} onClick={() => navigate('/risicos/overzicht')}
                        className={`p-3 rounded-lg border ${cfg.border} ${cfg.light} text-left hover:shadow-sm transition-all`}
                        data-testid={`severity-${level}`}>
                        <div className="flex items-center gap-2 mb-1">
                          <div className={`h-2 w-2 rounded-full ${cfg.dot}`} />
                          <span className="text-[10px] font-medium text-slate-500 capitalize">{level}</span>
                        </div>
                        <p className={`text-xl font-bold ${cfg.text}`}>{count}</p>
                      </button>
                    );
                  })}
                </div>
                {uncoveredRisks > 0 && (
                  <div className="flex items-center gap-2 mt-3 p-2.5 rounded-lg bg-amber-50 border border-amber-200">
                    <AlertTriangle className="h-3.5 w-3.5 text-amber-600 shrink-0" strokeWidth={1.5} />
                    <span className="text-xs text-amber-700"><strong>{uncoveredRisks}</strong> risico's zonder gekoppelde maatregel</span>
                    <Button variant="ghost" size="sm" className="text-xs h-6 ml-auto text-amber-700 hover:text-amber-900" onClick={() => navigate('/verbeteren/maatregelen')}>Oplossen</Button>
                  </div>
                )}
              </div>

              {/* DOMAIN MATURITY HEATMAP */}
              <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="domain-heatmap">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <Gauge className="h-4 w-4 text-slate-400" strokeWidth={1.5} />
                    <h3 className="text-sm font-semibold text-slate-800">Volwassenheid per domein</h3>
                  </div>
                  <Button variant="ghost" size="sm" className="text-xs h-7 text-slate-500" onClick={() => navigate('/beoordelen/volwassenheid')} data-testid="go-to-maturity">
                    Beoordeel <ChevronRight className="h-3 w-3 ml-0.5" />
                  </Button>
                </div>
                <div className="grid grid-cols-6 gap-2">
                  {Object.entries(scoreData?.maturity?.domain_levels || {}).map(([dk, level]) => {
                    const progress = scoreData?.measure_progress?.[dk];
                    return (
                      <button key={dk} onClick={() => navigate('/beoordelen/volwassenheid')}
                        className="p-3 rounded-lg border border-slate-100 bg-slate-50/50 hover:border-slate-200 transition-all text-center"
                        data-testid={`domain-${dk}`}>
                        <p className="text-[10px] font-medium text-slate-500 truncate mb-1.5">{DOMAIN_LABELS[dk]}</p>
                        <div className={`text-xl font-bold mx-auto h-9 w-9 rounded-lg flex items-center justify-center ${
                          level != null ? (level >= 4 ? 'bg-emerald-100 text-emerald-700' : level >= 3 ? 'bg-blue-100 text-blue-700' : level >= 2 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700') : 'bg-slate-100 text-slate-300'
                        }`}>
                          {level ?? '-'}
                        </div>
                        {progress && (
                          <div className="mt-2">
                            <div className="h-1 bg-slate-200 rounded-full overflow-hidden">
                              <div className="h-full bg-emerald-400 rounded-full" style={{ width: `${progress.percentage}%` }} />
                            </div>
                            <p className="text-[9px] text-slate-400 mt-0.5">{progress.percentage}%</p>
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* TREND CHART */}
              {trendData?.trends?.length >= 2 && (
                <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="trend-chart">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <TrendingUp className="h-4 w-4 text-slate-400" strokeWidth={1.5} />
                      <h3 className="text-sm font-semibold text-slate-800">Trend</h3>
                    </div>
                    <div className="flex items-center gap-3 text-[10px] text-slate-400">
                      <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-400 inline-block" />Risico's</span>
                      <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-400 inline-block" />Opgelost</span>
                    </div>
                  </div>
                  <div className="h-44">
                    <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={0}>
                      <AreaChart data={trendData.trends} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
                        <defs>
                          <linearGradient id="riskG" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#f87171" stopOpacity={0.1}/><stop offset="100%" stopColor="#f87171" stopOpacity={0}/></linearGradient>
                          <linearGradient id="mitG" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#34d399" stopOpacity={0.1}/><stop offset="100%" stopColor="#34d399" stopOpacity={0}/></linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                        <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                        <YAxis tick={{ fontSize: 10, fill: '#94a3b8' }} axisLine={false} tickLine={false} allowDecimals={false} />
                        <RechartsTooltip contentStyle={{ border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '11px', boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }} />
                        <Area type="monotone" dataKey="total_risks" stroke="#f87171" fill="url(#riskG)" strokeWidth={1.5} dot={false} name="Risico's" />
                        <Area type="monotone" dataKey="mitigated" stroke="#34d399" fill="url(#mitG)" strokeWidth={1.5} dot={false} name="Opgelost" />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}

              {/* COMPLIANCE FRAMEWORKS */}
              {Object.keys(scoreData?.compliance || {}).length > 0 && (
                <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="compliance-section">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <Lock className="h-4 w-4 text-slate-400" strokeWidth={1.5} />
                      <h3 className="text-sm font-semibold text-slate-800">Compliance</h3>
                    </div>
                    <Button variant="ghost" size="sm" className="text-xs h-7 text-slate-500" onClick={downloadCompliancePDF}>
                      <Download className="h-3 w-3 mr-1" /> Export PDF
                    </Button>
                  </div>
                  <div className="space-y-3">
                    {Object.entries(scoreData.compliance).map(([fwId, data]) => (
                      <div key={fwId} className="flex items-center gap-4">
                        <div className="w-24 shrink-0"><p className="text-xs font-medium text-slate-700 truncate">{fwId}</p></div>
                        <div className="flex-1">
                          <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                            <div className={`h-full rounded-full transition-all ${data.score >= 95 ? 'bg-emerald-500' : data.score >= 80 ? 'bg-amber-500' : 'bg-red-500'}`} style={{ width: `${data.score}%` }} />
                          </div>
                        </div>
                        <span className={`text-xs font-bold tabular-nums w-12 text-right ${data.score >= 95 ? 'text-emerald-600' : data.score >= 80 ? 'text-amber-600' : 'text-red-600'}`}>{Math.round(data.score)}%</span>
                        <span className="text-[10px] text-slate-400 w-16 text-right">{data.met}/{data.total}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* RIGHT COLUMN (1/3) */}
            <div className="space-y-4">

              {/* NULMETING PROGRESS */}
              <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="assessment-card">
                <div className="flex items-center gap-2 mb-3">
                  <Activity className="h-4 w-4 text-slate-400" strokeWidth={1.5} />
                  <h3 className="text-sm font-semibold text-slate-800">Nulmeting</h3>
                </div>
                <div className="relative h-2 bg-slate-100 rounded-full overflow-hidden mb-3">
                  <div className="h-full bg-slate-900 rounded-full transition-all" style={{ width: `${nulmetingPct}%` }} />
                </div>
                <p className="text-xs text-slate-500 mb-3">{nulmetingPct}% voltooid</p>
                {progressData?.domain_progress && (
                  <div className="space-y-2">
                    {Object.entries(progressData.domain_progress).map(([dk, info]) => (
                      <div key={dk} className="flex items-center justify-between">
                        <span className="text-xs text-slate-600">{info.label}</span>
                        {info.done ? (
                          <CheckCircle className="h-4 w-4 text-emerald-500" strokeWidth={1.5} />
                        ) : (
                          <span className="text-[10px] text-slate-400">Niet gestart</span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                {nulmetingPct < 100 && (
                  <Button className="w-full mt-4 bg-slate-900 text-white text-xs h-9" onClick={() => navigate('/beoordelen/nulmeting')} data-testid="start-assessment-btn">
                    {nulmetingPct > 0 ? 'Verder met analyse' : 'Start nulmeting'} <ArrowRight className="h-3.5 w-3.5 ml-1.5" />
                  </Button>
                )}
              </div>

              {/* WERKLIJST — persoonlijke takenlijst */}
              <div className="bg-white rounded-xl border border-[#E5E7EB] p-5" data-testid="action-items">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-semibold text-[#111827]">Jouw werklijst</h3>
                  {actionItems.length > 0 && <span className="text-xs font-medium text-[#6B7280] bg-[#F3F4F6] px-2 py-0.5 rounded-full">{actionItems.length}</span>}
                </div>

                {/* Smart task list */}
                <div className="space-y-1">
                  {/* Dynamische taken gebaseerd op data */}
                  {(risksByLevel.kritiek + risksByLevel.hoog) > 0 && (
                    <button onClick={() => navigate('/risicos/overzicht')} className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-[#F9FAFB] text-left transition-colors group" data-testid="task-risks">
                      <span className="h-6 w-6 rounded-md bg-[#FEF2F2] text-[#B91C1C] text-xs font-bold flex items-center justify-center shrink-0">{risksByLevel.kritiek + risksByLevel.hoog}</span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-[#111827]">Kritieke risico's beoordelen</p>
                        <p className="text-xs text-[#6B7280]">{risksByLevel.kritiek} kritiek, {risksByLevel.hoog} hoog</p>
                      </div>
                      <ChevronRight className="h-4 w-4 text-[#D1D5DB] group-hover:text-[#6B7280]" />
                    </button>
                  )}
                  {uncoveredRisks > 0 && (
                    <button onClick={() => navigate('/verbeteren/maatregelen')} className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-[#F9FAFB] text-left transition-colors group" data-testid="task-measures">
                      <span className="h-6 w-6 rounded-md bg-[#FFFBEB] text-[#92400E] text-xs font-bold flex items-center justify-center shrink-0">{uncoveredRisks}</span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-[#111827]">Maatregelen koppelen</p>
                        <p className="text-xs text-[#6B7280]">{uncoveredRisks} risico's zonder maatregel</p>
                      </div>
                      <ChevronRight className="h-4 w-4 text-[#D1D5DB] group-hover:text-[#6B7280]" />
                    </button>
                  )}
                  {nulmetingPct < 100 && (
                    <button onClick={() => navigate('/beoordelen/nulmeting')} className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-[#F9FAFB] text-left transition-colors group" data-testid="task-assessment">
                      <span className="h-6 w-6 rounded-md bg-[#EFF6FF] text-[#1D4ED8] text-xs font-bold flex items-center justify-center shrink-0">{nulmetingPct}%</span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-[#111827]">Nulmeting afronden</p>
                        <p className="text-xs text-[#6B7280]">{100 - nulmetingPct}% resterend</p>
                      </div>
                      <ChevronRight className="h-4 w-4 text-[#D1D5DB] group-hover:text-[#6B7280]" />
                    </button>
                  )}
                  {/* Extra actiepunten van backend */}
                  {topActions.slice(0, 3).map((action, idx) => (
                    <button key={action.id || idx} onClick={() => action.link && navigate(action.link)}
                      className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-[#F9FAFB] text-left transition-colors group"
                      data-testid={`action-item-${idx}`}>
                      <span className={`h-6 w-6 rounded-md text-xs font-bold flex items-center justify-center shrink-0 ${
                        action.priority === 'high' ? 'bg-[#FEF2F2] text-[#B91C1C]' : 'bg-[#F3F4F6] text-[#4B5563]'
                      }`}>
                        {idx + 1}
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-[#111827] truncate">{action.title}</p>
                        {action.description && <p className="text-xs text-[#6B7280] line-clamp-1">{action.description}</p>}
                      </div>
                      <ChevronRight className="h-4 w-4 text-[#D1D5DB] group-hover:text-[#6B7280]" />
                    </button>
                  ))}
                  {(risksByLevel.kritiek + risksByLevel.hoog) === 0 && uncoveredRisks === 0 && nulmetingPct >= 100 && topActions.length === 0 && (
                    <div className="text-center py-8">
                      <CheckCircle className="h-8 w-8 text-[#047857] mx-auto mb-2" strokeWidth={1.5} />
                      <p className="text-sm font-medium text-[#111827]">Alles op orde</p>
                      <p className="text-xs text-[#6B7280] mt-0.5">Geen openstaande taken</p>
                    </div>
                  )}
                </div>
              </div>

              {/* QUICK LINKS */}
              <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="quick-links">
                <h3 className="text-sm font-semibold text-slate-800 mb-3">Rapporten</h3>
                <div className="space-y-1.5">
                  <button onClick={downloadPDF} disabled={pdfLoading} className="w-full flex items-center gap-3 p-2.5 rounded-lg hover:bg-slate-50 text-left transition-colors" data-testid="download-assessment-report">
                    <FileText className="h-4 w-4 text-slate-400" strokeWidth={1.5} />
                    <span className="text-xs text-slate-600">Assessment rapport (PDF)</span>
                  </button>
                  <button onClick={downloadCompliancePDF} className="w-full flex items-center gap-3 p-2.5 rounded-lg hover:bg-slate-50 text-left transition-colors" data-testid="download-compliance-report">
                    <Lock className="h-4 w-4 text-slate-400" strokeWidth={1.5} />
                    <span className="text-xs text-slate-600">Compliance rapport (PDF)</span>
                  </button>
                </div>
              </div>

            </div>
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

function RISKS_CLASSES_GET(cls) {
  return RISK_CLASSES[cls] || RISK_CLASSES.laag;
}

export default DashboardPage;
